import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { tripsService, vehiclesService, routesAPIService } from '../services/api';
import '../styles/HomeWidgets.css';

interface DashboardStats {
  trips: {
    total: number;
    active: number;
    completed: number;
    recent: any[];
  };
  routes: {
    total: number;
    recent: any[];
  };
  vehicles: {
    total: number;
    active: number;
    recent: any[];
  };
}

interface WidgetProps {
  title: string;
  icon: string;
  children: React.ReactNode;
  isLoading?: boolean;
}

const Widget: React.FC<WidgetProps> = ({ title, icon, children, isLoading = false }) => {
  if (isLoading) {
    return (
      <div className="widget-card">
        <div className="loading-widget">
          <div className="loading-spinner"></div>
          <div className="loading-text">Loading {title.toLowerCase()}...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="widget-card">
      <div className="widget-header">
        <div className="widget-icon">{icon}</div>
        <h3 className="widget-title">{title}</h3>
      </div>
      <div className="widget-content">
        {children}
      </div>
    </div>
  );
};

function HomePage() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [dashboardStats, setDashboardStats] = useState<DashboardStats>({
    trips: { total: 0, active: 0, completed: 0, recent: [] },
    routes: { total: 0, recent: [] },
    vehicles: { total: 0, active: 0, recent: [] }
  });
  const [isLoadingStats, setIsLoadingStats] = useState(true);

  // Check if user has driver role
  const isDriver = user?.roles?.some(role => role.name === 'driver') || false;
  const isAdmin = user?.roles?.some(role => role.name === 'admin') || false;

  useEffect(() => {
    if (isAuthenticated && user) {
      loadDashboardData();
    }
  }, [isAuthenticated, user]);

  const loadDashboardData = async () => {
    try {
      setIsLoadingStats(true);
      
      // Load trips data
      const [driverTrips, passengerTrips, routes, vehicles] = await Promise.all([
        tripsService.getMyTrips('driver').catch(() => ({ trips: [] })),
        tripsService.getMyTrips('passenger').catch(() => ({ trips: [] })),
        routesAPIService.getAllRoutes({ limit: 5 }).catch(() => ({ routes: [] })),
        isDriver ? vehiclesService.getMyVehicles().catch(() => ({ vehicles: [] })) : Promise.resolve({ vehicles: [] })
      ]);

      // Process trips statistics
      const allTrips = [...driverTrips.trips, ...passengerTrips.trips];
      const activeTrips = allTrips.filter(trip => trip.status === 'active');
      const completedTrips = allTrips.filter(trip => trip.status === 'completed');

      setDashboardStats({
        trips: {
          total: allTrips.length,
          active: activeTrips.length,
          completed: completedTrips.length,
          recent: allTrips.slice(0, 3)
        },
        routes: {
          total: routes.routes?.length || 0,
          recent: routes.routes?.slice(0, 3) || []
        },
        vehicles: {
          total: vehicles.vehicles?.length || 0,
          active: vehicles.vehicles?.filter((v: any) => v.is_active)?.length || 0,
          recent: vehicles.vehicles?.slice(0, 3) || []
        }
      });
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setIsLoadingStats(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string) => {
    return <span className={`status-badge ${status}`}>{status}</span>;
  };

  const onSearch = (query: string) => {
    console.log('Searching for:', query);
    navigate(`/routes/search/${encodeURIComponent(query)}`);
  };

  const SearchBar = () => (
    <div className="search-bar">
      <label htmlFor="search-input" className="search-label">🔍 Where do you want to go?</label>
      <div className="search-input-container">
        <input
          id="search-input"
          type="text"
          className="search-input"
          placeholder="Search for routes, trips, destinations..."
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              // Implement search functionality
              console.log('Searching for:', (e.target as HTMLInputElement).value);
              onSearch((e.target as HTMLInputElement).value);
            }
          }}
        />
        <button 
          className="search-button" 
          onClick={() => {
            const input = document.querySelector('.search-input') as HTMLInputElement;
            console.log('Searching for:', input.value);
            onSearch(input.value);
          }}
          type="button"
          aria-label="Search"
        >
          🔍
        </button>
      </div>
    </div>
  );

  // Dashboard component for authenticated users
  const Dashboard = () => (
    <div className="dashboard-container">
      <h2 className="dashboard-title">Welcome back, {[user?.first_name, user?.last_name].filter(Boolean).join(' ')}! 👋</h2>
      {/* Search where you want to go  */}
      <SearchBar />
      <p className="dashboard-subtitle">Here's an overview of your activity</p>

      <div className="widgets-grid">
        {/* Trips Widget */}
        <Widget title="My Trips" icon="🚗" isLoading={isLoadingStats}>
          <div className="stats-row">
            <div className="stat-item">
              <span className="stat-number">{dashboardStats.trips.total}</span>
              <span className="stat-label">Total Trips</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{dashboardStats.trips.active}</span>
              <span className="stat-label">Active</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{dashboardStats.trips.completed}</span>
              <span className="stat-label">Completed</span>
            </div>
          </div>

          <div className="widget-actions">
            <Link to="/trips" className="widget-btn primary">
              <span>📋</span> View All Trips
            </Link>
            {user?.roles?.some(role => role.name === 'driver') && (
              <Link to="/trips/create" className="widget-btn secondary">
                <span>➕</span> Post Trip
              </Link>
            )}
          </div>

          {dashboardStats.trips.recent.length > 0 ? (
            <div className="recent-items">
              <div className="recent-items-title">Recent Trips</div>
              {dashboardStats.trips.recent.map((trip: any) => (
                <div key={trip.id} className="recent-item">
                  <div className="recent-item-icon">🛣️</div>
                  <div className="recent-item-content">
                    <div className="recent-item-title">
                      {trip.route_name || (trip.start_location && trip.destination ? `${trip.start_location} → ${trip.destination}` : `Trip ${trip.id}`)}
                    </div>
                    <div className="recent-item-subtitle">
                      {formatDate(trip.departure_time)} • {getStatusBadge(trip.status)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">✈️</div>
              <div className="empty-state-title">No trips yet</div>
              <div className="empty-state-text">Start by posting your first trip or joining one!</div>
            </div>
          )}
        </Widget>

        {/* Routes Widget */}
        <Widget title="My Routes" icon="🗺️" isLoading={isLoadingStats}>
          <div className="stats-row">
            <div className="stat-item">
              <span className="stat-number">{dashboardStats.routes.total}</span>
              <span className="stat-label">Available Routes</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">
                {dashboardStats.routes.recent.filter((r: any) => r.is_public).length}
              </span>
              <span className="stat-label">Public Routes</span>
            </div>
          </div>

          <div className="quick-actions">
            <Link to="/routes" className="quick-action">
              <span className="quick-action-icon">🔍</span>
              <span className="quick-action-label">Search</span>
            </Link>
            {user?.roles?.some(role => role.name === 'driver') && (
              <Link to="/routes/create" className="quick-action">
                <span className="quick-action-icon">➕</span>
                <span className="quick-action-label">Create</span>
              </Link>
            )}
            <Link to="/map" className="quick-action" style={{display: 'none'}}>
              <span className="quick-action-icon">🗺️</span>
              <span className="quick-action-label">Map View</span>
            </Link>
          </div>

          <div className="widget-actions">
            <Link to="/routes" className="widget-btn primary">
              <span>📍</span> Explore Routes
            </Link>
          </div>

          {dashboardStats.routes.recent.length > 0 ? (
            <div className="recent-items">
              <div className="recent-items-title">Popular Routes</div>
              {dashboardStats.routes.recent.map((route: any) => (
                <div key={route.id} className="recent-item">
                  <div className="recent-item-icon">📍</div>
                  <div className="recent-item-content">
                    <div className="recent-item-title">{route.name}</div>
                    <div className="recent-item-subtitle">
                      {route.start_point_name} → {route.end_point_name}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">🗺️</div>
              <div className="empty-state-title">No routes found</div>
              <div className="empty-state-text">Explore available routes or create your own!</div>
            </div>
          )}
        </Widget>

        {/* Vehicles Widget - Only for drivers */}
        {isDriver && (
          <Widget title="My Vehicles" icon="🚙" isLoading={isLoadingStats}>
            <div className="stats-row">
              <div className="stat-item">
                <span className="stat-number">{dashboardStats.vehicles.total}</span>
                <span className="stat-label">Total Vehicles</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{dashboardStats.vehicles.active}</span>
                <span className="stat-label">Active</span>
              </div>
            </div>

            <div className="widget-actions">
              <Link to="/vehicles" className="widget-btn primary">
                <span>🚗</span> Manage Vehicles
              </Link>
              <Link to="/vehicles/new" className="widget-btn secondary">
                <span>➕</span> Add Vehicle
              </Link>
            </div>

            {dashboardStats.vehicles.recent.length > 0 ? (
              <div className="recent-items">
                <div className="recent-items-title">My Vehicles</div>
                {dashboardStats.vehicles.recent.map((vehicle: any) => (
                  <div key={vehicle.id} className="recent-item">
                    <div className="recent-item-icon">🚗</div>
                    <div className="recent-item-content">
                      <div className="recent-item-title">
                        {vehicle.make} {vehicle.model}
                      </div>
                      <div className="recent-item-subtitle">
                        {vehicle.license_plate} • {vehicle.color}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <div className="empty-state-icon">🚙</div>
                <div className="empty-state-title">No vehicles added</div>
                <div className="empty-state-text">Add your first vehicle to start posting trips!</div>
              </div>
            )}
          </Widget>
        )}

        {/* Quick Access Widget - Always visible */}
        <Widget title="Quick Access" icon="⚡">
          <div className="quick-actions">
            <Link to="/chat" className="quick-action" style={{display: 'none'}}>
              <span className="quick-action-icon">💬</span>
              <span className="quick-action-label">Chat</span>
            </Link>
            <Link to="/map" className="quick-action" style={{display: 'none'}}>
              <span className="quick-action-icon">🗺️</span>
              <span className="quick-action-label">Maps</span>
            </Link>
            <Link to="/services" className="quick-action">
              <span className="quick-action-icon">🛠️</span>
              <span className="quick-action-label">Services</span>
            </Link>
            <Link to="/trips" className="quick-action">
              <span className="quick-action-icon">🚗</span>
              <span className="quick-action-label">Trips</span>
            </Link>
            <Link to="/profile" className="quick-action">
              <span className="quick-action-icon">👤</span>
              <span className="quick-action-label">Profile</span>
            </Link>
          </div>

          <div className="widget-actions">
            <Link to="/help" className="widget-btn secondary">
              <span>❓</span> Get Help
            </Link>
            {!user?.roles?.some(role => role.name === 'driver') && (
              <Link to="/apply-driver" className="widget-btn secondary">
                <span>🚘</span> Become a driver
              </Link>
            )}
            <Link to="/settings" className="widget-btn secondary">
              <span>⚙️</span> Settings
            </Link>
          </div>
        </Widget>
      </div>
    </div>
  );

  return (
    <div>
      {isAuthenticated ? (
        <Dashboard />
      ) : (
        <>
          <main className="main-content">
            <section className="landing-hero">
              <div className="container landing-hero-inner">
                <span className="landing-kicker">Transportation + Services Marketplace</span>
                <h1 className="landing-hero-title">
                  One platform for shared rides, service requests, payments, and trusted community workflows.
                </h1>
                <p className="landing-hero-subtitle">
                  DandiaLift connects passengers, drivers, and service providers in a single ecosystem.
                  Plan routes, book seats, request local services, manage bids, and track activity from one account.
                </p>
                <div className="landing-hero-actions">
                  <Link to="/register" className="landing-btn landing-btn-primary">
                    Create Account
                  </Link>
                  <Link to="/login" className="landing-btn landing-btn-outline">
                    Sign In
                  </Link>
                </div>
              </div>
            </section>

            <section className="landing-section landing-light">
              <div className="container">
                <h2 className="landing-title">What You Can Do Here</h2>
                <p className="landing-subtitle">Designed for daily travel needs and on-demand local services.</p>

                <div className="landing-grid landing-grid-3">
                  <article className="landing-card">
                    <div className="landing-card-icon">🚗</div>
                    <h3>Ride-Sharing and Trips</h3>
                    <p>
                      Create reusable routes, publish one-time or recurring trips, accept passenger requests,
                      and manage trip history from your dashboard.
                    </p>
                    <Link to="/login" className="landing-inline-link">Explore trips</Link>
                  </article>

                  <article className="landing-card">
                    <div className="landing-card-icon">🛠️</div>
                    <h3>Services Marketplace</h3>
                    <p>
                      Post service requests with budgets and deadlines, receive competitive bids from providers,
                      and choose the right professional for the job.
                    </p>
                    <Link to="/login" className="landing-inline-link">Browse services</Link>
                  </article>

                  <article className="landing-card">
                    <div className="landing-card-icon">💳</div>
                    <h3>Wallet and Payments</h3>
                    <p>
                      Handle trip and service transactions with integrated wallet features, transaction history,
                      and M-Pesa support for deposits and withdrawals.
                    </p>
                    <Link to="/login" className="landing-inline-link">View payment tools</Link>
                  </article>

                  <article className="landing-card">
                    <div className="landing-card-icon">⭐</div>
                    <h3>Ratings and Trust</h3>
                    <p>
                      Build reputation through ratings and reviews across drivers, passengers, and service providers.
                      Better visibility comes from consistent quality.
                    </p>
                  </article>

                  <article className="landing-card">
                    <div className="landing-card-icon">💬</div>
                    <h3>Real-Time Communication</h3>
                    <p>
                      Use in-app chat and live updates for trips, service bids, and coordination events,
                      so everyone stays informed in real time.
                    </p>
                  </article>

                  <article className="landing-card">
                    <div className="landing-card-icon">🛡️</div>
                    <h3>Role-Based Access and Review</h3>
                    <p>
                      Support for driver applications, service provider applications, role assignment,
                      and administration workflows keeps operations controlled and transparent.
                    </p>
                  </article>
                </div>
              </div>
            </section>

            <section className="landing-section">
              <div className="container">
                <h2 className="landing-title">How It Works</h2>
                <div className="landing-grid landing-grid-3">
                  <article className="landing-step">
                    <span className="landing-step-number">1</span>
                    <h3>Join and Choose Your Path</h3>
                    <p>
                      Register once, then use the platform as a passenger, apply to become a driver,
                      or apply as a service provider based on your goals.
                    </p>
                  </article>
                  <article className="landing-step">
                    <span className="landing-step-number">2</span>
                    <h3>Post, Browse, and Match</h3>
                    <p>
                      Post trips or services, browse open opportunities, send requests or bids,
                      and confirm participation with clear workflow steps.
                    </p>
                  </article>
                  <article className="landing-step">
                    <span className="landing-step-number">3</span>
                    <h3>Pay, Track, and Review</h3>
                    <p>
                      Complete secure payments, follow activity updates, and submit ratings and feedback
                      to strengthen trust across the network.
                    </p>
                  </article>
                </div>
              </div>
            </section>

            <section className="landing-section landing-accent">
              <div className="container landing-role-wrap">
                <div>
                  <h2 className="landing-title">Built for Every Participant</h2>
                  <ul className="landing-list">
                    <li><strong>Passengers:</strong> Find trips, request seats, and travel smarter.</li>
                    <li><strong>Drivers:</strong> Publish routes, manage vehicles, and fill seats.</li>
                    <li><strong>Service Requesters:</strong> Post needs and compare bids.</li>
                    <li><strong>Service Providers:</strong> Bid on jobs and grow reputation.</li>
                    <li><strong>Admins:</strong> Review applications and manage platform roles.</li>
                  </ul>
                </div>
                <div className="landing-role-actions">
                  <Link to="/register" className="landing-btn landing-btn-primary">Apply as Driver</Link>
                  <Link to="/register" className="landing-btn landing-btn-secondary">Apply as Service Provider</Link>
                </div>
              </div>
            </section>

            <section className="landing-section landing-cta">
              <div className="container">
                <h2 className="landing-title">Start with Trips. Grow with Services.</h2>
                <p className="landing-subtitle">
                  Whether you are commuting, offering rides, or building a service business,
                  Routes Share gives you the tools to operate in one connected platform.
                </p>
                <div className="landing-hero-actions">
                  <Link to="/register" className="landing-btn landing-btn-primary">Get Started</Link>
                  <Link to="/login" className="landing-btn landing-btn-outline">I Already Have an Account</Link>
                </div>
              </div>
            </section>
          </main>
        </>
      )}
    </div>
  );
}

export default HomePage;

